import type {
  LateCancelListResponseDto,
  LateCancelListStatus,
  LateCancelRecordDto,
} from '@shared/core/cancellation-policy'
import { SnackbarKey, useSnackbar } from 'notistack'
import { useCallback, useContext, useEffect, useState } from 'react'
import { PaginationContext } from 'src/modules/core/context'
import type { PaginationContextValue } from 'src/modules/core/context/pagination-context/pagination-context.inerface'
import {
  useLateCancelCounts,
  useLateCancels,
  useResolveLateCancel,
  useUnresolveLateCancel,
} from '../../hooks/use-late-cancels'
import { lateCancelClientLabel } from '../../utils/late-cancel-display.util'
import classes from './late-cancels-wrapper.module.scss'

const RESOLVE_FAILED = "Couldn't mark the late cancel as resolved. Please try again."
const UNDO_FAILED = "Couldn't move the late cancel back to Open. Please try again."

function useSyncPagination(list: LateCancelListResponseDto | undefined, pagination: PaginationContextValue) {
  const { currentPage, setPage, setNumberOfPages } = pagination
  useEffect(() => {
    if (!list) return
    const pages = Math.max(1, Math.ceil(list.total / list.limit))
    setNumberOfPages(pages)
    if (currentPage > pages) setPage(pages)
  }, [list, currentPage, setPage, setNumberOfPages])
}

function useLateCancelActions() {
  const resolve = useResolveLateCancel()
  const unresolve = useUnresolveLateCancel()
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const { mutateAsync: resolveAsync } = resolve
  const { mutate: unresolveMutate } = unresolve

  const undo = useCallback(
    (record: LateCancelRecordDto) =>
      unresolveMutate(record.id, {
        onError: () => {
          enqueueSnackbar(UNDO_FAILED, { variant: 'error' })
        },
      }),
    [unresolveMutate, enqueueSnackbar],
  )

  const markResolved = useCallback(
    async (record: LateCancelRecordDto) => {
      try {
        await resolveAsync(record.id)
      } catch {
        enqueueSnackbar(RESOLVE_FAILED, { variant: 'error' })
        return
      }
      const undoAction = (key: SnackbarKey) => (
        <button
          type="button"
          className={classes.toastUndo}
          data-testid="late-cancel-toast-undo"
          onClick={() => {
            closeSnackbar(key)
            undo(record)
          }}
        >
          Undo
        </button>
      )
      enqueueSnackbar(`${lateCancelClientLabel(record)} marked as resolved`, { variant: 'success', action: undoAction })
    },
    [resolveAsync, enqueueSnackbar, closeSnackbar, undo],
  )

  let pendingId: string | null = null
  if (resolve.isLoading) pendingId = resolve.variables ?? null
  else if (unresolve.isLoading) pendingId = unresolve.variables ?? null

  return { markResolved, undo, pendingId }
}

export function useLateCancelsWrapper() {
  const [status, setStatus] = useState<LateCancelListStatus>('open')
  const pagination = useContext(PaginationContext)
  const countsQuery = useLateCancelCounts()
  const listQuery = useLateCancels({ status, page: pagination.currentPage })
  useSyncPagination(listQuery.data, pagination)
  const actions = useLateCancelActions()
  const { setPage } = pagination

  const changeStatus = useCallback(
    (next: LateCancelListStatus) => {
      setStatus(next)
      setPage(1)
    },
    [setPage],
  )

  return {
    ...actions,
    status,
    changeStatus,
    counts: countsQuery.data,
    list: listQuery.data,
    isLoading: countsQuery.isLoading || listQuery.isLoading,
    isError: countsQuery.isError || listQuery.isError,
    numberOfPages: pagination.numberOfPages,
  }
}

export type LateCancelsWrapperState = ReturnType<typeof useLateCancelsWrapper>
